import { useEffect, useRef, useState, useMemo } from 'react';
import { X, Copy, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSecretHistory } from '@/hooks/useSecretHistory';

type DiffSegment = 
  | { type: 'equal'; text: string }
  | { type: 'add'; text: string }
  | { type: 'delete'; text: string };

// LCS-based character diff algorithm with O(m*n) complexity
function diffStrings(oldStr: string, newStr: string): DiffSegment[] {
  if (oldStr === newStr) return oldStr ? [{ type: 'equal', text: oldStr }] : [];
  if (!oldStr) return newStr ? [{ type: 'add', text: newStr }] : [];
  if (!newStr) return [{ type: 'delete', text: oldStr }];

  const m = oldStr.length;
  const n = newStr.length;
  
  // Fallback for very long strings to avoid memory issues
  if (m * n > 1000000) {
    return [{ type: 'delete', text: oldStr }, { type: 'add', text: newStr }];
  }

  // Build LCS dynamic programming table
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldStr[i - 1] === newStr[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  // Backtrack through LCS table to build diff operations
  const ops: Array<{ type: 'equal' | 'add' | 'delete'; char: string }> = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldStr[i - 1] === newStr[j - 1]) {
      ops.push({ type: 'equal', char: oldStr[i - 1] });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      ops.push({ type: 'add', char: newStr[j - 1] });
      j--;
    } else {
      ops.push({ type: 'delete', char: oldStr[i - 1] });
      i--;
    }
  }
  ops.reverse();

  // Merge consecutive same-type operations
  const segments: DiffSegment[] = [];
  for (const op of ops) {
    const last = segments[segments.length - 1];
    if (last && last.type === op.type) {
      last.text += op.char;
    } else {
      segments.push({ type: op.type, text: op.char });
    }
  }
  return segments;
}

function DiffView({ segments }: { segments: DiffSegment[] }) {
  if (segments.length === 0) {
    return <span className="text-muted-foreground italic">empty</span>;
  }

  return (
    <>
      {segments.map((segment, idx) => {
        switch (segment.type) {
          case 'equal':
            return <span key={idx} className="text-muted-foreground">{segment.text}</span>;
          case 'add':
            return (
              <span key={idx} className="bg-success/20 text-success rounded-sm px-0.5">
                {segment.text}
              </span>
            );
          case 'delete':
            return (
              <span key={idx} className="bg-destructive/20 text-destructive line-through rounded-sm px-0.5">
                {segment.text}
              </span>
            );
        }
      })}
    </>
  );
}

interface HistoryPanelProps {
  secretKey: string;
  currentValue?: string;
  projectId: string;
  environmentSlug: string;
  onClose: () => void;
  onCopy: (value: string) => void;
  embedded?: boolean;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function HistoryPanel({
  secretKey,
  currentValue,
  projectId,
  environmentSlug,
  onClose,
  onCopy,
  embedded = false,
}: HistoryPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { history, isLoading, error } = useSecretHistory(
    projectId,
    environmentSlug,
    secretKey
  );

  const historyWithDiffs = useMemo(() => {
    if (!history.length) return [];
    
    return history.map((entry, index) => {
      const olderEntry = history[index + 1];
      const diffSegments = olderEntry
        ? diffStrings(olderEntry.value, entry.value)
        : null;
      return { ...entry, diffSegments };
    });
  }, [history]);

  const currentValueDiff = useMemo(() => {
    if (currentValue === undefined || !history.length) return null;
    const latestHistoryValue = history[0]?.value;
    if (latestHistoryValue === undefined || latestHistoryValue === currentValue) return null;
    return diffStrings(latestHistoryValue, currentValue);
  }, [currentValue, history]);

  const handleCopy = (value: string, id: string) => {
    onCopy(value);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Close on Escape
  useEffect(() => {
    if (embedded) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, embedded]);

  // Close on click outside
  useEffect(() => {
    if (embedded) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Delay to prevent immediate close
    const timeout = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, embedded]);

  const content = (
    <div className={`flex-1 overflow-y-auto ${embedded ? 'py-4' : 'p-8'}`}>
      {/* Current value */}
      {currentValue !== undefined && (
        <div className="mb-8 relative ml-1.5 border-l border-transparent pl-6">
          <div className="absolute left-[-30px] top-3 h-3 w-3 rounded-full bg-success ring-4 ring-background" />
          <div className="text-xs font-medium text-success mb-2 uppercase tracking-wider">Current Version</div>
          <div className="bg-card/50 border border-success/20 rounded-lg p-4 relative group transition-all hover:border-success/40">
            <pre className="font-mono text-sm whitespace-pre-wrap break-all">
              {currentValueDiff ? (
                <DiffView segments={currentValueDiff} />
              ) : (
                <span className="text-foreground">{currentValue}</span>
              )}
            </pre>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopy(currentValue, 'current')}
              className="absolute top-2 right-2 h-7 w-7 p-0 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
              title="Copy"
            >
              {copiedId === 'current' ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      )}

      {/* Version history */}
      <div className="relative border-l border-border ml-1.5 pl-6 space-y-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-muted-foreground">
            Failed to load history
          </div>
        ) : historyWithDiffs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No version history
          </div>
        ) : (
          historyWithDiffs.map((entry) => {
            const isFirstVersion = entry.diffSegments === null;

            return (
              <div key={entry.id} className="relative">
                <div className="absolute left-[-30px] top-3 h-3 w-3 rounded-full bg-border ring-4 ring-background" />

                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-muted-foreground bg-card px-1.5 py-0.5 rounded border border-border">
                      v{entry.version}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(entry.changed_at)}
                    </span>
                    {isFirstVersion && (
                      <span className="text-xs text-muted-foreground/60 italic">
                        initial
                      </span>
                    )}
                  </div>
                  {entry.changed_by && (
                    <span className="text-xs text-muted-foreground/60">
                      by {entry.changed_by}
                    </span>
                  )}
                </div>

                <div className="bg-card rounded-lg p-4 relative group border border-border hover:border-border/80 transition-colors">
                  <pre className="font-mono text-sm whitespace-pre-wrap break-all">
                    {entry.diffSegments ? (
                      <DiffView segments={entry.diffSegments} />
                    ) : (
                      <span className="text-muted-foreground">{entry.value}</span>
                    )}
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(entry.value, entry.id)}
                    className="absolute top-2 right-2 h-7 w-7 p-0 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Copy"
                  >
                    {copiedId === entry.id ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity" />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed top-0 right-0 h-full w-[600px] bg-background border-l border-border z-50 overflow-hidden flex flex-col shadow-2xl animate-in slide-in-from-right duration-300"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-background/50 backdrop-blur">
          <div>
            <h2 className="font-mono font-medium text-foreground truncate text-lg">
              {secretKey}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">Version History</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        {content}
      </div>
    </>
  );
}
